/**
 * A saved table layout, read against the columns the table has today.
 *
 * What is saved is somebody's arrangement: the column order, what is hidden,
 * widths they dragged, how many columns stay frozen, the sort, the page size
 * and the row density. It was saved against whatever columns existed then,
 * and the code moves on underneath it - a column is added, another is only
 * there when the data has something to put in it (the AI score), a second
 * device runs last week's build. So the saved value is never trusted to
 * describe the table; it is woven into it:
 *
 *   - ids the table no longer has are ignored, but kept in what is saved,
 *     so a column that comes back comes back where it was;
 *   - a column the saved order has never heard of appears where its default
 *     position puts it, with its default visibility, rather than vanishing
 *     or landing at the far end;
 *   - a width is only stored when somebody changed it, so a better default
 *     width in the code still reaches everyone who never touched it.
 *
 * Pure: no React, so the rules can be run in Node.
 */

export const PAGE_SIZES = [25, 50, 100, 250];
export const MAX_FROZEN = 4;
export const MIN_WIDTH = 60;
export const MAX_WIDTH = 900;
export const DEFAULT_WIDTH = 160;

/** The keys that are the layout, as opposed to anything a page keeps beside it. */
export const LAYOUT_KEYS = ['order', 'hidden', 'sizes', 'frozen', 'sort', 'pageSize', 'density'];

/** Columns that stay against the right edge - a row's own buttons. They are
    never hidden, moved, filtered, sorted or exported. */
export const isPinnedRight = c => !!(c && c.meta && c.meta.pin === 'right');

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const numberOr = (v, fallback) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/**
 * Put into `into` every id from `from` that it lacks and `want` accepts,
 * each straight after the nearest id that came before it in `from`.
 *
 * One rule serves both directions: new columns woven into a saved order,
 * and a saved order's unknown ids woven back into a freshly arranged one.
 */
export function weave(into, from, want = () => true) {
  const out = [...into];
  const have = new Set(out);
  (from || []).forEach((id, i) => {
    if (have.has(id) || !want(id)) return;
    let j = i - 1;
    while (j >= 0 && !have.has(from[j])) j -= 1;
    out.splice(j < 0 ? 0 : out.indexOf(from[j]) + 1, 0, id);
    have.add(id);
  });
  return out;
}

const unique = list => [...new Set(list)];

/** The width a column has when nobody has dragged it. */
export function defaultWidth(column, defaults) {
  const set = defaults && defaults.sizes ? defaults.sizes[column.id] : undefined;
  return clamp(
    numberOr(set, numberOr(column.size, DEFAULT_WIDTH)),
    numberOr(column.minSize, MIN_WIDTH),
    numberOr(column.maxSize, MAX_WIDTH)
  );
}

/**
 * The layout to draw: `saved` (possibly stale, possibly nothing) read against
 * `columns`, with `defaults` filling whatever it does not say.
 *
 * Returns { order, hidden (a Set), sizes, frozen, sort, pageSize, density }.
 * `order` holds every movable column; pinned-right ones are never in it.
 */
export function resolveLayout(columns, saved, defaults = {}) {
  const movable = columns.filter(c => !isPinnedRight(c));
  const ids = movable.map(c => c.id);
  const known = new Set(ids);
  const s = saved && typeof saved === 'object' ? saved : {};
  const d = defaults || {};

  /* The default order is the page's list, or the order the columns were
     declared in for anything that list leaves out. */
  const fallback = weave(unique((d.order || []).filter(id => known.has(id))), ids);
  const savedOrder = Array.isArray(s.order) ? unique(s.order.filter(id => known.has(id))) : null;
  const order = savedOrder ? weave(savedOrder, fallback) : fallback;

  /* Hidden is only believed about the columns the saved order knew; one it
     has never heard of is new, and takes the default. */
  const knewOf = new Set(Array.isArray(s.order) ? s.order : []);
  const savedHidden = new Set(Array.isArray(s.hidden) ? s.hidden : []);
  const defHidden = new Set(d.hidden || []);
  const hidden = new Set(ids.filter(id => (
    savedOrder && knewOf.has(id) ? savedHidden.has(id) : defHidden.has(id)
  )));
  /* A table with every column hidden is a table nobody can get back into. */
  if (ids.length && hidden.size >= ids.length) hidden.delete(order[0]);

  const sizes = {};
  movable.forEach(c => {
    const own = s.sizes && typeof s.sizes === 'object' ? s.sizes[c.id] : undefined;
    sizes[c.id] = clamp(
      numberOr(own, defaultWidth(c, d)),
      numberOr(c.minSize, MIN_WIDTH),
      numberOr(c.maxSize, MAX_WIDTH)
    );
  });
  columns.filter(isPinnedRight).forEach(c => { sizes[c.id] = numberOr(c.size, 120); });

  const frozen = clamp(Math.round(numberOr(s.frozen, numberOr(d.frozen, 0))), 0, MAX_FROZEN);

  const sortable = new Set(movable.filter(c => c.enableSorting !== false).map(c => c.id));
  const sortFrom = Array.isArray(s.sort) ? s.sort : (Array.isArray(d.sort) ? d.sort : []);
  const seen = new Set();
  const sort = sortFrom
    .filter(x => x && sortable.has(x.id) && !seen.has(x.id) && seen.add(x.id))
    .map(x => ({ id: x.id, desc: !!x.desc }));

  const pageSize = PAGE_SIZES.includes(s.pageSize) ? s.pageSize
    : PAGE_SIZES.includes(d.pageSize) ? d.pageSize : PAGE_SIZES[0];
  const density = (s.density || d.density) === 'compact' ? 'compact' : 'comfortable';

  return { order, hidden, sizes, frozen, sort, pageSize, density };
}

/**
 * A layout as it is stored: `next` for the columns the table has, and
 * whatever `prev` said about columns it does not have, carried over.
 */
export function toSaved(next, prev, columns, defaults = {}) {
  const known = new Set(columns.map(c => c.id));
  const unknown = id => !known.has(id);
  const p = prev && typeof prev === 'object' ? prev : {};

  const order = weave(next.order, Array.isArray(p.order) ? p.order : [], unknown);
  const hidden = [
    ...next.order.filter(id => next.hidden.has(id)),
    ...(Array.isArray(p.hidden) ? p.hidden.filter(unknown) : [])
  ];

  const sizes = {};
  if (p.sizes && typeof p.sizes === 'object') {
    Object.keys(p.sizes).filter(unknown).forEach(id => { sizes[id] = p.sizes[id]; });
  }
  columns.filter(c => !isPinnedRight(c)).forEach(c => {
    const w = Math.round(next.sizes[c.id]);
    if (Number.isFinite(w) && w !== Math.round(defaultWidth(c, defaults))) sizes[c.id] = w;
  });

  return {
    order,
    hidden,
    sizes,
    frozen: next.frozen,
    sort: next.sort.map(x => ({ id: x.id, desc: !!x.desc })),
    pageSize: next.pageSize,
    density: next.density
  };
}

/** `order` with `id` moved to just before or after `target`. */
export function moveColumn(order, id, target, side = 'before') {
  if (id === target || !order.includes(id) || !order.includes(target)) return order;
  const out = order.filter(x => x !== id);
  const at = out.indexOf(target) + (side === 'after' ? 1 : 0);
  out.splice(at, 0, id);
  return out;
}

/** `order` with `id` swapped one place towards the start (-1) or the end (+1),
    stepping over hidden columns so a move is always a visible one. */
export function nudgeColumn(order, hidden, id, step) {
  const shown = order.filter(x => !hidden.has(x));
  const i = shown.indexOf(id);
  const j = i + step;
  if (i < 0 || j < 0 || j >= shown.length) return order;
  return moveColumn(order, id, shown[j], step < 0 ? 'before' : 'after');
}
